import type { CatalogV2Product, ProductRewardSearchResult } from '@brandclub/common-ui';
import { graphqlFetch } from './graphqlFetch';
import { adaptCatalogV2ProductToRewardSearchResult } from './catalogV2ProductToLegacyRewardSearchResultMappers';

export const PRODUCT_V2_BY_RETAILER_SKU = 'ProductV2ByRetailerSku';
const PRODUCT_V2_BY_RETAILER_SKU_FIELD = 'productV2ByRetailerSku';

const normalizeRetailerSku = (value: string): string => {
  const prefix = 'gid://shopify/ProductVariant/';
  const trimmed = value.trim();
  return (trimmed.startsWith(prefix) ? trimmed.slice(prefix.length) : trimmed).split(/[?#]/)[0] ?? '';
};

export const PRODUCT_V2_BY_RETAILER_SKU_QUERY = /* GraphQL */ `
  query ${PRODUCT_V2_BY_RETAILER_SKU}($storeKind: String!, $retailerSku: String!) {
    productV2ByRetailerSku(storeKind: $storeKind, retailerSku: $retailerSku) {
      stacklineSku
      catalogSku
      parentStacklineSku
      retailerId
      retailerSku
      retailerName
      retailerProductPageUrl
      title
      imageUrl
      imageUrls
      brandId
      brandName
      retailPrice
      compareAtPrice
      isAvailable
      availabilityStatus
      checkoutEligible
      isLinkedToInventory
      amazonSku
      primeDeliveryTargetId
      whereToBuyFallback
      whereToBuyOfferRetailerIds
      rewardAmount
      campaignRewards {
        campaignId
        campaignType
        campaignStatus
        rewardAmount
        rewardPercent
        rewardAmountType
        rewardUnlockStrategy
      }
      offers {
        retailerId
        retailerSku
        stacklineSku
        catalogSku
        isPrimary
        channelType
        retailerName
        title
        retailPrice
        compareAtPrice
        retailerProductPageUrl
        imageUrls
        inStock
        rewardAmount
      }
      variants {
        stacklineSku
        title
        retailPrice
        compareAtPrice
        imageUrl
        imageUrls
        retailerProductPageUrl
        isAvailable
        availabilityStatus
        checkoutEligible
        isLinkedToInventory
        amazonSku
        primeDeliveryTargetId
        whereToBuyFallback
        whereToBuyOfferRetailerIds
        options { name value }
        offers {
          retailerId
          retailerSku
          stacklineSku
          catalogSku
          isPrimary
          channelType
          retailerName
          title
          retailPrice
          compareAtPrice
          retailerProductPageUrl
          imageUrls
          inStock
          rewardAmount
        }
      }
    }
  }
`;

export const getCatalogV2ProductByRetailerSku = async (
  retailerSku: string,
  storeKind: string,
  signal?: AbortSignal,
): Promise<ProductRewardSearchResult> => {
  const normalizedRetailerSku = normalizeRetailerSku(retailerSku);
  if (!normalizedRetailerSku) throw new Error('Error fetching Catalog V2 product: missing retailerSku');
  const response = await graphqlFetch({
    operationName: PRODUCT_V2_BY_RETAILER_SKU,
    query: PRODUCT_V2_BY_RETAILER_SKU_QUERY,
    variables: { storeKind, retailerSku: normalizedRetailerSku },
    signal,
  });
  if (response?.errors?.length) {
    throw new Error(response.errors[0]?.message ?? 'Error fetching Catalog V2 product');
  }
  const product = response?.data?.[PRODUCT_V2_BY_RETAILER_SKU_FIELD] as CatalogV2Product | undefined;
  if (!product) throw new Error('Error fetching Catalog V2 product: product not found');
  return adaptCatalogV2ProductToRewardSearchResult(product, normalizedRetailerSku);
};
